import { useMemo, useState } from "react";
import {
  Autocomplete as MuiAutocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Box, Button, TextField, Typography } from "../../ds";
import { useUserSearch } from "../../hooks/useUserSearch";
import type { Criterion, MemberScore } from "./criteria";

const NUMERIC = { fontVariantNumeric: "tabular-nums" } as const;

/** A person a member row can name: a directory user. */
export interface MemberCandidate {
  id: string;
  name: string;
}

interface PickerOption extends MemberCandidate {
  group: string;
}

const COMMITTEE_GROUP = "On this committee";
const DIRECTORY_GROUP = "Directory";

interface MemberPickerProps {
  index: number;
  member: MemberScore;
  committee: MemberCandidate[];
  /** Users already picked on the other rows — each member scores once. */
  taken: ReadonlySet<string>;
  onChange: (member: Pick<MemberScore, "member_name" | "member_user_id">) => void;
}

/**
 * Who scored — always a directory user. The committee seated on this
 * suggestion is offered first (they are almost always the scorers); anyone
 * else is a search away, through the same user search the committee seat
 * pickers use. There is no free text: the backend requires a user id on
 * every row.
 *
 * MUI's Autocomplete rather than `ds/Autocomplete`: a table cell needs an
 * input with an accessible name but no floating label, which the wrapper's
 * `label`-driven input cannot give.
 */
function MemberPicker({
  index,
  member,
  committee,
  taken,
  onChange,
}: MemberPickerProps) {
  const [query, setQuery] = useState("");
  const { results, loading } = useUserSearch(query);

  const value = useMemo<PickerOption | null>(
    () =>
      member.member_user_id
        ? {
            id: member.member_user_id,
            name: member.member_name,
            group: DIRECTORY_GROUP,
          }
        : null,
    [member],
  );

  const options = useMemo<PickerOption[]>(() => {
    const seated = committee
      .filter((c) => !taken.has(c.id))
      .map((c) => ({ ...c, group: COMMITTEE_GROUP }));
    const seen = new Set(committee.map((c) => c.id));
    const searched = results
      .filter((r) => !seen.has(r.id) && !taken.has(r.id))
      .map((r) => ({ id: r.id, name: r.name, group: DIRECTORY_GROUP }));
    const all = [...seated, ...searched];
    // The picked user may have come from a search that is long gone; MUI
    // treats a value outside `options` as invalid.
    return value && !all.some((o) => o.id === value.id) ? [value, ...all] : all;
  }, [committee, results, taken, value]);

  return (
    <MuiAutocomplete<PickerOption>
      size="small"
      options={options}
      value={value}
      groupBy={(o) => o.group}
      loading={loading}
      noOptionsText={
        query.trim().length < 2 ? "Type at least 2 characters" : "No matches"
      }
      filterOptions={(opts, state) => {
        // Directory rows already answered the query server-side (by name
        // or employee ID); the handful of committee rows filter here.
        const q = state.inputValue.trim().toLowerCase();
        return opts.filter(
          (o) =>
            o.group === DIRECTORY_GROUP ||
            !q ||
            o.name.toLowerCase().includes(q) ||
            o.id === value?.id,
        );
      }}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_e, text, reason) => {
        if (reason === "input") setQuery(text);
      }}
      onChange={(_e, picked) =>
        onChange(
          picked
            ? { member_name: picked.name, member_user_id: picked.id }
            : { member_name: "", member_user_id: null },
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          // A row stored before members were picked keeps its typed name;
          // show it until someone picks the user it meant.
          placeholder={
            !value && member.member_name
              ? `${member.member_name} — pick the user`
              : "Search by name or employee ID"
          }
          inputProps={{
            ...params.inputProps,
            "aria-label": `Member ${index + 1}`,
          }}
        />
      )}
    />
  );
}

interface Props {
  criteria: readonly Criterion[];
  members: MemberScore[];
  /** People seated on this committee for the suggestion, offered first. */
  committee?: MemberCandidate[];
  /** Per-criterion committee score the rows average to. */
  averages: Record<string, number>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMemberChange: (
    index: number,
    member: Pick<MemberScore, "member_name" | "member_user_id">,
  ) => void;
  onScore: (index: number, key: string, raw: string) => void;
}

/**
 * Members down, criteria across — the tally sheet a committee fills in
 * together, with the average it produces closing the table. One row per
 * member beats one form per member: the point of scoring by member is
 * comparing marks on the same criterion, which only a column affords.
 */
export default function MemberMatrix({
  criteria,
  members,
  committee = [],
  averages,
  onAdd,
  onRemove,
  onMemberChange,
  onScore,
}: Props) {
  const columns = criteria.length + 2;
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflowX: "auto",
      }}
    >
      <Table size="small" sx={{ minWidth: 160 + criteria.length * 104 }}>
        <TableHead>
          <TableRow>
            {/* Same table-header treatment as the criteria sheet above. */}
            <TableCell sx={{ minWidth: 200 }}>
              <Typography variant="tableHeader" color="text.primary">
                Member
              </Typography>
            </TableCell>
            {criteria.map((c) => (
              <TableCell key={c.key} align="right" sx={{ minWidth: 96 }}>
                <Typography variant="tableHeader" color="text.primary">
                  {c.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  max {c.max}
                </Typography>
              </TableCell>
            ))}
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {members.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns}>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Add each member who scored, then enter their marks.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            members.map((m, index) => (
              <TableRow key={index}>
                <TableCell>
                  <MemberPicker
                    index={index}
                    member={m}
                    committee={committee}
                    taken={
                      new Set(
                        members
                          .filter((_, i) => i !== index)
                          .map((o) => o.member_user_id)
                          .filter((id): id is string => !!id),
                      )
                    }
                    onChange={(next) => onMemberChange(index, next)}
                  />
                </TableCell>
                {criteria.map((c) => (
                  <TableCell key={c.key} align="right">
                    <TextField
                      type="number"
                      value={m.scores[c.key] ?? ""}
                      onChange={(e) => onScore(index, c.key, e.target.value)}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: c.max,
                          step: 1,
                          "aria-label": `Member ${index + 1} ${c.label}`,
                          style: { textAlign: "right", ...NUMERIC },
                        },
                      }}
                    />
                  </TableCell>
                ))}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label={`Remove member ${index + 1}`}
                    onClick={() => onRemove(index)}
                  >
                    <RavenIcon icon={Trash2} sx={{ fontSize: 16 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
            >
              Average
            </TableCell>
            {criteria.map((c) => (
              <TableCell
                key={c.key}
                align="right"
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "text.primary",
                  ...NUMERIC,
                }}
              >
                {averages[c.key] ?? 0}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell colSpan={columns} sx={{ borderBottom: "none" }}>
              <Button
                variant="text"
                onClick={onAdd}
                sx={{ color: "primary.dark", minWidth: "auto", px: 1 }}
              >
                + Add member
              </Button>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Box>
  );
}
